import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { getSubAccounts } from "src/api/subAccount";
import { useAuth } from "src/providers/AuthProvider";
import CustomModal from "src/components/CustomModal";
import AddSubAccountWithParent from "src/components/modals/AddSubAccountWithParent";
import { Button } from "src/components/ui/button";
import { DataTable, PageHead, SearchField, type Column, type RowsPerPageOption } from "src/components/common";
import { getCustomizationValue } from "src/lib/utils";
import type { SubAccount } from "src/types/customer";

const SubAccounts = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const subAccountLabel = getCustomizationValue(user, "sub_account_label", true) || "Sub Account";
	const subAccountLabelPlural = getCustomizationValue(user, "sub_account_label") || "Sub Accounts";
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const search = searchParams.get("search") ?? undefined;
	const page = Number(searchParams.get("page") ?? 1);
	const limitParam = searchParams.get("limit") ?? "10";
	const limit: RowsPerPageOption = limitParam === "All" ? "All" : Number(limitParam);

	const { data, isLoading } = useQuery({
		queryKey: ["subAccounts", page, limit, search],
		queryFn: () => getSubAccounts({ page, limit: limit === "All" ? 9999 : limit, search }),
	});

	const list = data?.data;
	const subAccounts = list?.data ?? [];
	const total = list?.total ?? subAccounts.length;

	const columns: Column<SubAccount>[] = [
		{ key: "first", header: "First Name", primary: true, cell: (row) => row.firstname ?? "—" },
		{ key: "last", header: "Last Name", cell: (row) => row.lastname ?? "—" },
		{ key: "id", header: "ID Number", cell: (row) => row.idNo ?? "—" },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[130px] tw-text-right",
			cell: (row) => (
				<button
					type="button"
					// Customer detail is a modal on the customers page now (decision D2).
					onClick={() => navigate(`/customers?customer=${row.uuid ?? ""}`)}
					className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
				>
					View {customerLabel}
				</button>
			),
		},
	];

	return (
		<div className="tw-space-y-1">
			<PageHead
				title={subAccountLabelPlural}
				subtitle={isLoading ? "Loading..." : `${total} ${total === 1 ? subAccountLabel.toLowerCase() : subAccountLabelPlural.toLowerCase()}`}
				actions={
					<CustomModal
						title={`Add ${subAccountLabel}`}
						trigger={
							<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
								<Plus className="tw-h-4 tw-w-4" />
								Add {subAccountLabel}
							</Button>
						}
					>
						<AddSubAccountWithParent />
					</CustomModal>
				}
			/>

			<div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3 tw-py-4">
				<SearchField placeholder={`Search ${subAccountLabelPlural.toLowerCase()}...`} />
			</div>

			<DataTable
				data={subAccounts}
				columns={columns}
				rowKey={(row, index) => row.uuid ?? index}
				loading={isLoading}
				totalRows={total}
				page={page}
				onPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("page", String(next));
					setSearchParams(params);
				}}
				rowsPerPage={limit}
				onRowsPerPageChange={(next) => {
					const params = new URLSearchParams(searchParams);
					params.set("limit", String(next));
					params.set("page", "1");
					setSearchParams(params);
				}}
				emptyTitle={`No ${subAccountLabelPlural.toLowerCase()} yet`}
				emptyDescription={`${subAccountLabelPlural} linked to your ${customerLabel.toLowerCase()}s will appear here.`}
			/>
		</div>
	);
};

export default SubAccounts;
